/**
 * Simple memoization utility for expensive calculations
 * Caches results based on a stringified version of the arguments
 */

/**
 * Creates a memoized version of a function
 * @param {Function} fn - The function to memoize
 * @param {Function} keyGenerator - Optional custom key generator
 * @returns {Function} - Memoized function
 */
export const memoize = (fn, keyGenerator = null) => {
  const cache = new Map();
  
  return function(...args) {
    // Generate cache key
    const key = keyGenerator 
      ? keyGenerator(...args)
      : JSON.stringify(args);
    
    // Return cached result if available
    if (cache.has(key)) {
      console.log(`[Memoize] Cache hit for ${fn.name}`);
      return cache.get(key);
    }
    
    // Calculate and cache result
    console.log(`[Memoize] Cache miss for ${fn.name}, calculating...`);
    const result = fn.apply(this, args);
    cache.set(key, result);
    
    // Limit cache size to prevent memory issues
    if (cache.size > 50) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    return result;
  };
};

/**
 * Generates a cache key from formData for nutrition/pace calculations
 * Only includes relevant fields to avoid cache misses from irrelevant changes
 */
export const generateFormDataKey = (formData) => {
  if (!formData) return 'null';
  
  // Extract only the fields that affect calculations
  const relevantFields = {
    distance: formData.distance,
    distanceUnit: formData.distanceUnit,
    paceModel: formData.paceModel || 'even',
    goalTime: formData.goalTime,
    cutoffTime: formData.cutoffTime,
    nutritionType: formData.nutritionType,
    gelFrequency: formData.gelFrequency,
    gelFrequencyUnit: formData.gelFrequencyUnit,
    carbsPerHour: formData.carbsPerHour,
    preRaceFuelEnabled: formData.preRaceFuelEnabled,
    preRaceFuelDescription: formData.preRaceFuelDescription,
    preRaceGelEquivalents: formData.preRaceGelEquivalents,
    preRaceGel: formData.preRaceGel,
    // Aid stations (but not their names, just locations)
    aidStations: formData.aidStations?.map(as => ({
      location: as.location,
      hasDropBag: as.hasDropBag,
      hasNate: as.hasNate,
      isFinish: as.isFinish,
      stopTimeMinutes: as.stopTimeMinutes,
      aidStopDurationHours: as.aidStopDurationHours,
      aidStopDurationMinutes: as.aidStopDurationMinutes
    })),
    // Additional fuel sources
    additionalFuelSources: formData.additionalFuelSources,
    fuelSources: formData.fuelSources,
    // Elevation data (just length, not full array)
    hasElevationData: !!formData.elevationData && formData.elevationData.length > 0,
    elevationDataLength: formData.elevationData?.length || 0
  };
  
  return JSON.stringify(relevantFields);
};

export default memoize;

